import React, { useCallback, useEffect, useState } from "react";
import { Button, message } from "antd";
import { CheckOutlined } from "@ant-design/icons";

import { controller as rootController } from "../../controller";
import { isPendingId, pendingSource } from "./pendingPolicies";
import CarrierResolution from "./CarrierResolution";
import { initialChoice, isChoiceComplete } from "./carrierChoice";

import PolicyCriticalFields from "../TreatmentPlans/component/shared/PolicyCriticalFields";
import { savePolicyDrafts } from "../TreatmentPlans/component/shared/PolicyCompletionStep";
import { REVIEW_FIELDS, describeMissing, missingCriticalFields } from "../TreatmentPlans/lib/policyCompleteness";

/**
 * Reviewing and approving a policy where it is picked — the wizard's Policies
 * step opens this inside each selected card.
 *
 * Approving is a real write, not a UI flag: it runs the same `savePolicyDrafts`
 * the modals use on submit, which PATCHes ONLY genuinely changed fields (every
 * field sent is appended to `manually_edited_fields` and locked against future
 * PMS syncs, so writing untouched values would quietly freeze them). A policy
 * that arrived complete from the PMS therefore approves with no network call.
 *
 * A pending card (a PMS policy with an unmatched carrier, or a plan read off an
 * uploaded claim form) has no row behind it yet: approving is what CREATES it,
 * once the carrier is known — the host's `onSavePending` does that.
 *
 * Editing an approved policy un-approves it — the host does that in onChange —
 * so "Approved" always means "what the carrier will receive is exactly what was
 * saved", never "this was fine a few keystrokes ago".
 */

function carrierOf(policy) {
  return policy.insurance_name_display || policy.insurance_name || "this policy";
}

/**
 * The state approving needs across the step: the carrier chosen for each
 * pending card, a "we already have this carrier" answer, which card is saving.
 * Kept at the step, not in each card, so collapsing a card keeps its choice.
 */
export function usePolicyReview({ policies, drafts, onApproved, onSavePending }) {
  const list = policies || [];
  const [approvingId, setApprovingId] = useState(null);
  // policyId -> CarrierResolution choice, for pending cards only.
  const [carrierChoice, setCarrierChoice] = useState({});
  // policyId -> "we already have this carrier" answer from the save.
  const [carrierConflict, setCarrierConflict] = useState({});
  const [providers, setProviders] = useState([]);

  const hasPending = list.some(function (p) { return isPendingId(p.id); });

  // The full carrier list, so the answer isn't limited to what matching
  // guessed. Only fetched when something actually needs it.
  useEffect(
    function () {
      if (!hasPending || providers.length) return undefined;
      let cancelled = false;
      (async function () {
        try {
          const res = await rootController.listInsuranceProviders();
          const rows = (res && res.data) || [];
          if (!cancelled && Array.isArray(rows)) setProviders(rows);
        } catch (e) {
          // The ranked candidates from the match are still offered.
          console.error(e);
        }
      })();
      return function () { cancelled = true; };
    },
    [hasPending, providers.length],
  );

  // Seed each pending card from what the backend knows: a carrier we recognise,
  // "add it" prefilled with the printed name for one we don't, nothing for a
  // placeholder. Weak scored guesses are never pre-selected — they were most of
  // the wrong picks on prod.
  useEffect(
    function () {
      setCarrierChoice(function (prev) {
        let next = prev;
        list.forEach(function (p) {
          if (!isPendingId(p.id) || prev[p.id] !== undefined) return;
          if (next === prev) next = Object.assign({}, prev);
          next[p.id] = initialChoice(p.__preview);
        });
        return next;
      });
    },
    [list],
  );

  const chooseCarrier = useCallback(function (policyId, value) {
    setCarrierChoice(function (prev) { return Object.assign({}, prev, { [policyId]: value }); });
    setCarrierConflict(function (prev) { return Object.assign({}, prev, { [policyId]: null }); });
  }, []);

  function effective(policy) {
    return Object.assign({}, policy, (drafts && drafts[policy.id]) || {});
  }

  function needsCarrier(policy) {
    return isPendingId(policy.id) && !isChoiceComplete(carrierChoice[policy.id]);
  }

  async function approve(policy) {
    const merged = effective(policy);
    if (missingCriticalFields(merged).length) return;

    if (isPendingId(policy.id)) {
      const choice = carrierChoice[policy.id];
      if (!isChoiceComplete(choice) || !onSavePending) return;
      setApprovingId(policy.id);
      try {
        const saved = await onSavePending(policy, choice, (drafts && drafts[policy.id]) || {});
        if (saved && saved.conflict) {
          // "We already have this carrier" — answered inside the card.
          setCarrierConflict(function (prev) { return Object.assign({}, prev, { [policy.id]: saved.conflict }); });
          return;
        }
        if (!saved || saved.error !== undefined) {
          message.error((saved && saved.error) || ("Couldn't save " + carrierOf(policy) + ". Please try again."));
          return;
        }
        onApproved(policy.id, null, saved);
      } catch (e) {
        console.error(e);
        message.error("Couldn't save " + carrierOf(policy) + ". Please try again.");
      } finally {
        setApprovingId(null);
      }
      return;
    }

    setApprovingId(policy.id);
    try {
      const ok = await savePolicyDrafts([policy], drafts);
      if (!ok) {
        message.error("Couldn't save " + carrierOf(policy) + ". Please try again.");
        return;
      }
      // Hand back what is now on the server so the host can fold it into the
      // policy and drop the draft — leaving nothing for submit to re-save.
      // REVIEW_FIELDS: relation_to_member is saved with the rest, so it has to
      // come back with the rest.
      const saved = {};
      REVIEW_FIELDS.forEach(function (field) {
        saved[field] = merged[field];
      });
      onApproved(policy.id, saved);
    } catch (e) {
      console.error(e);
      message.error("Couldn't save " + carrierOf(policy) + ". Please try again.");
    } finally {
      setApprovingId(null);
    }
  }

  return {
    approvingId,
    carrierChoice,
    carrierConflict,
    providers,
    chooseCarrier,
    effective,
    needsCarrier,
    approve,
  };
}

/** What a policy still owes before it can be approved, in a few words. */
export function reviewState(policy, review, isApproved) {
  if (isApproved) return "Approved";
  const missing = missingCriticalFields(review.effective(policy));
  if (missing.length) return "Needs " + describeMissing(missing);
  if (review.needsCarrier(policy)) return "Needs a carrier";
  return "Ready to approve";
}

/**
 * One policy's review: the carrier (a pending card only), the critical fields
 * with any PMS / claim-form suggestions, and Approve.
 */
export function PolicyReviewBody({
  policy, review, drafts, isApproved, preview, patientId, patient, onChange,
}) {
  const merged = review.effective(policy);
  const missing = missingCriticalFields(merged);
  const pending = isPendingId(policy.id);
  const needsCarrier = review.needsCarrier(policy);
  // Inside the drawer, so pickers must render next to their input rather than
  // portalling to document.body, where they never become reachable.
  const popup = function (trigger) { return trigger.parentElement; };

  return (
    <div className="ir-check-body">
      {pending ? (
        <div className="ir-carrier-pick">
          <div className="ir-field-label">Insurance carrier</div>
          <CarrierResolution
            preview={policy.__preview}
            providers={review.providers.length ? review.providers : undefined}
            choice={review.carrierChoice[policy.id]}
            conflict={review.carrierConflict[policy.id]}
            onChange={function (value) { review.chooseCarrier(policy.id, value); }}
            getPopupContainer={popup}
          />
        </div>
      ) : null}

      <PolicyCriticalFields
        policyId={policy.id}
        merged={merged}
        draft={(drafts && drafts[policy.id]) || {}}
        missing={missing}
        preview={policy.__preview || preview || null}
        patientId={patientId}
        patient={patient}
        onChange={onChange}
        getPopupContainer={popup}
      />

      {isApproved ? (
        <div className="ir-check-done-note">
          <CheckOutlined /> Saved and approved — edit any field to change it.
        </div>
      ) : (
        <div className="ir-check-actions">
          <span className="ir-check-hint">
            {missing.length
              ? "Add " + describeMissing(missing) + " to approve."
              : needsCarrier
                ? "Choose or add the carrier to approve."
                : pending
                  ? (pendingSource(policy) === "claim_form"
                    ? "Approving adds this policy from the claim form."
                    : "Approving adds this policy from your PMS.")
                  : "Approving saves these details to the policy."}
          </span>
          <Button
            type="primary"
            loading={review.approvingId === policy.id}
            disabled={missing.length > 0 || needsCarrier}
            onClick={function () { review.approve(policy); }}
          >
            Approve
          </Button>
        </div>
      )}
    </div>
  );
}
